import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import apiService from '../services/api';

const TABS = [
  { key: 'basic', label: 'Basic Info', icon: '👤' },
  { key: 'personal', label: 'Personal Details', icon: '📋' },
  { key: 'bank', label: 'Bank Details', icon: '🏦' },
  { key: 'security', label: 'Security', icon: '🔒' },
];

const Profile = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [tabAnimate, setTabAnimate] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [formData, setFormData] = useState({
    prefix: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    language: '',
    profilePhoto: null,
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    bloodGroup: '',
    mobileNumber: '',
    alternateContact: '',
    familyContact: '',
    facebookLink: '',
    twitterLink: '',
    socialMedia1: '',
    socialMedia2: '',
    customField1: '',
    customField2: '',
    customField3: '',
    customField4: '',
    guardianName: '',
    idProofName: '',
    idProofNumber: '',
    permanentAddress: '',
    currentAddress: '',
    accountHolderName: '',
    accountNumber: '',
    bankName: '',
    bankIdentifierCode: '',
    branch: '',
    taxPayerId: ''
  });

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const user = await apiService.getCurrentUser();
        if (user) {
          let bankDetails = {};
          try {
            if (user.bank_details) {
              bankDetails = typeof user.bank_details === 'string' ? JSON.parse(user.bank_details) : user.bank_details;
            }
          } catch (e) { bankDetails = {}; }

          setFormData(prev => ({
            ...prev,
            prefix: user.surname || 'Mr',
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            email: user.email || '',
            language: user.language || '',
            profilePhoto: user.profile_photo || null,
            dateOfBirth: user.dob || '',
            gender: user.gender || '',
            maritalStatus: user.marital_status || '',
            bloodGroup: user.blood_group || '',
            mobileNumber: user.contact_number || '',
            alternateContact: user.alt_number || '',
            familyContact: user.family_number || '',
            facebookLink: user.fb_link || '',
            twitterLink: user.twitter_link || '',
            socialMedia1: user.social_media_1 || '',
            socialMedia2: user.social_media_2 || '',
            customField1: user.custom_field_1 || '',
            customField2: user.custom_field_2 || '',
            customField3: user.custom_field_3 || '',
            customField4: user.custom_field_4 || '',
            guardianName: user.guardian_name || '',
            idProofName: user.id_proof_name || '',
            idProofNumber: user.id_proof_number || '',
            permanentAddress: user.permanent_address || '',
            currentAddress: user.current_address || '',
            accountHolderName: bankDetails.account_holder_name || '',
            accountNumber: bankDetails.account_number || '',
            bankName: bankDetails.bank_name || '',
            bankIdentifierCode: bankDetails.bank_code || '',
            branch: bankDetails.branch || '',
            taxPayerId: bankDetails.tax_payer_id || ''
          }));
          if (user.profile_photo) {
            setImagePreview(user.profile_photo);
          }
        }
      } catch (error) {
      /* console.error('Error loading profile:', error); */
      }
    };
    loadUserProfile();
  }, []);

  const switchTab = (key) => { setTabAnimate(true); setActiveTab(key); setTimeout(() => setTabAnimate(false), 300); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setFormData(prev => ({ ...prev, profilePhoto: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New password and confirm password do not match');
      return;
    }
    
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      alert('Please fill in all password fields');
      return;
    }

    setIsLoading(true);
    try {
      await apiService.changePassword(passwordData);
      setShowSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      /* console.error('Error changing password:', error); */
      alert('Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formDataToSend = new FormData();

      const fieldMapping = {
        prefix: 'surname',
        firstName: 'first_name',
        lastName: 'last_name',
        email: 'email',
        language: 'language',
        dateOfBirth: 'dob',
        gender: 'gender',
        maritalStatus: 'marital_status',
        bloodGroup: 'blood_group',
        mobileNumber: 'contact_number',
        alternateContact: 'alt_number',
        familyContact: 'family_number',
        facebookLink: 'fb_link',
        twitterLink: 'twitter_link',
        socialMedia1: 'social_media_1',
        socialMedia2: 'social_media_2',
        customField1: 'custom_field_1',
        customField2: 'custom_field_2',
        customField3: 'custom_field_3',
        customField4: 'custom_field_4',
        guardianName: 'guardian_name',
        idProofName: 'id_proof_name',
        idProofNumber: 'id_proof_number',
        permanentAddress: 'permanent_address',
        currentAddress: 'current_address'
      };

      Object.keys(fieldMapping).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(fieldMapping[key], formData[key]);
        }
      });

      const bankDetails = {
        account_holder_name: formData.accountHolderName,
        account_number: formData.accountNumber,
        bank_name: formData.bankName,
        bank_code: formData.bankIdentifierCode,
        branch: formData.branch,
        tax_payer_id: formData.taxPayerId
      };
      formDataToSend.append('bank_details', JSON.stringify(bankDetails));

      if (formData.profilePhoto && formData.profilePhoto instanceof File) {
        formDataToSend.append('profile_photo', formData.profilePhoto);
      }

      await apiService.updateUserProfile(formDataToSend);

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      /* console.error('Error updating profile:', error); */
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="custom-profile-page">
      <div className="profile-header">
        <button className="back-button" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <div className="profile-title">
          <h1>My Profile</h1>
          <span className="profile-subtitle">Manage your personal information and security settings</span>
        </div>
      </div>

      {showSuccess && (
        <div className="success-toast">
          <span className="toast-icon">✓</span>
          <span>Updated successfully!</span>
        </div>
      )}

      <div className="profile-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => switchTab(tab.key)}
            type="button"
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="profile-content">

      {activeTab === 'security' ? (
      <div className={`tab-panel ${tabAnimate ? 'tab-animate-in' : ''}`}>
      <form onSubmit={handlePasswordSubmit} className="profile-form">
        <div className="form-section">
          <h2>Change Password</h2>
          
          <div className="form-row three-col">
            <div className="form-group">
              <label>Current password:</label>
              <div className="password-input-wrapper">
                <svg className="lock-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Current password"
                />
              </div>
            </div>
            <div className="form-group">
              <label>New password:</label>
              <div className="password-input-wrapper">
                <svg className="lock-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="New password"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Confirm new password:</label>
              <div className="password-input-wrapper">
                <svg className="lock-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="update-button" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>
      </div>
      ) : null}

      {activeTab !== 'security' ? (
      <div className={`tab-panel ${tabAnimate ? 'tab-animate-in' : ''}`}>
      <form onSubmit={handleSubmit} className="profile-form">
        {activeTab === 'basic' && (
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-row three-col">
            <div className="form-group">
              <label>Prefix:</label>
              <select name="prefix" value={formData.prefix} onChange={handleChange}>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
                <option value="Dr">Dr</option>
              </select>
            </div>
            <div className="form-group">
              <label>First Name:</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First Name"
              />
            </div>
            <div className="form-group">
              <label>Last Name:</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last Name"
              />
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="leftyliqour@gmail.com"
              />
            </div>
            <div className="form-group">
              <label>Language:</label>
              <input
                type="text"
                name="language"
                value={formData.language}
                onChange={handleChange}
                placeholder="Language"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width avatar-group">
              <label>Profile Photo</label>
              <div className="avatar-upload-wrapper">
                <div className="avatar-circle">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Profile preview" />
                  ) : (
                    <span className="avatar-placeholder">
                      {(formData.firstName?.[0] || '') + (formData.lastName?.[0] || '')}
                    </span>
                  )}
                  <label htmlFor="profilePhoto" className="avatar-upload-btn" title="Upload photo">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                      <circle cx="12" cy="13" r="4"></circle>
                    </svg>
                  </label>
                </div>
                <div className="avatar-meta">
                  <span className="avatar-label">Upload new image</span>
                  <span className="avatar-hint">Max file size: 5MB (JPG, PNG)</span>
                </div>
                <input
                  type="file"
                  id="profilePhoto"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'personal' && (
        <div className="form-section">
          <h2>Personal Details</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date of birth:</label>
              <input
                type="text"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                placeholder="Date of birth"
              />
            </div>
            <div className="form-group">
              <label>Gender:</label>
              <select name="gender" value={formData.gender} onChange={handleChange}>
                <option value="">Please Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Marital Status:</label>
              <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
                <option value="">Marital Status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Blood Group:</label>
              <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}>
                <option value="">Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Mobile Number:</label>
              <input
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                placeholder="Mobile Number"
              />
            </div>
            <div className="form-group">
              <label>Alternate contact number:</label>
              <input
                type="tel"
                name="alternateContact"
                value={formData.alternateContact}
                onChange={handleChange}
                placeholder="Alternate contact number"
              />
            </div>
            <div className="form-group">
              <label>Family contact number:</label>
              <input
                type="tel"
                name="familyContact"
                value={formData.familyContact}
                onChange={handleChange}
                placeholder="Family contact number"
              />
            </div>
            <div className="form-group">
              <label>Facebook Link:</label>
              <input
                type="text"
                name="facebookLink"
                value={formData.facebookLink}
                onChange={handleChange}
                placeholder="Facebook Link"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Twitter Link:</label>
              <input
                type="text"
                name="twitterLink"
                value={formData.twitterLink}
                onChange={handleChange}
                placeholder="Twitter Link"
              />
            </div>
            <div className="form-group">
              <label>Social Media 1:</label>
              <input
                type="text"
                name="socialMedia1"
                value={formData.socialMedia1}
                onChange={handleChange}
                placeholder="Social Media 1"
              />
            </div>
            <div className="form-group">
              <label>Social Media 2:</label>
              <input
                type="text"
                name="socialMedia2"
                value={formData.socialMedia2}
                onChange={handleChange}
                placeholder="Social Media 2"
              />
            </div>
            <div className="form-group">
              <label>Custom field 1:</label>
              <input
                type="text"
                name="customField1"
                value={formData.customField1}
                onChange={handleChange}
                placeholder="Custom field 1"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Custom field 2:</label>
              <input
                type="text"
                name="customField2"
                value={formData.customField2}
                onChange={handleChange}
                placeholder="Custom field 2"
              />
            </div>
            <div className="form-group">
              <label>Guardian Name:</label>
              <input
                type="text"
                name="guardianName"
                value={formData.guardianName}
                onChange={handleChange}
                placeholder="Guardian Name"
              />
            </div>
            <div className="form-group">
              <label>ID proof name:</label>
              <input
                type="text"
                name="idProofName"
                value={formData.idProofName}
                onChange={handleChange}
                placeholder="ID proof name"
              />
            </div>
            <div className="form-group">
              <label>ID proof number:</label>
              <input
                type="text"
                name="idProofNumber"
                value={formData.idProofNumber}
                onChange={handleChange}
                placeholder="ID proof number"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Permanent Address:</label>
              <textarea
                name="permanentAddress"
                value={formData.permanentAddress}
                onChange={handleChange}
                placeholder="Permanent Address"
                rows="3"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Current Address:</label>
              <textarea
                name="currentAddress"
                value={formData.currentAddress}
                onChange={handleChange}
                placeholder="Current Address"
                rows="3"
              />
            </div>
          </div>
        </div>
        )}

        {activeTab === 'bank' && (
        <div className="form-section">
          <h2>Bank Details</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Account Holder's Name:</label>
              <input
                type="text"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleChange}
                placeholder="Account Holder's Name"
              />
            </div>
            <div className="form-group">
              <label>Account Number:</label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                placeholder="Account Number"
              />
            </div>
            <div className="form-group">
              <label>Bank Name:</label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                placeholder="Bank Name"
              />
            </div>
            <div className="form-group">
              <label>Bank Identifier Code:</label>
              <input
                type="text"
                name="bankIdentifierCode"
                value={formData.bankIdentifierCode}
                onChange={handleChange}
                placeholder="Bank Identifier Code"
              />
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-group">
              <label>Branch:</label>
              <input
                type="text"
                name="branch"
                value={formData.branch}
                onChange={handleChange}
                placeholder="Branch"
              />
            </div>
            <div className="form-group">
              <label>Tax Payer ID:</label>
              <input
                type="text"
                name="taxPayerId"
                value={formData.taxPayerId}
                onChange={handleChange}
                placeholder="Tax Payer ID"
              />
            </div>
          </div>
        </div>
        )}

        <div className="form-actions">
          <button type="submit" className="update-button" disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>
      </div>
      ) : null}
      </div>
    </div>
  );
};

export default Profile;
